import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import BookingAction from "store/booking/BookingAction";
import BookingLoginInput from "pages/Front/Booking/components/BookingLoginInput";
import {FieldErrors} from "react-hook-form/dist/types/errors";
import {useHistory} from "react-router";
import {consoleLog, REGEX_EMAIL} from "utils/utility";
import BookingLoginErrors from "pages/Front/Booking/BookingLogin/components/BookingLoginErrors";
import {LOGIN_CODE_LENGTH} from "helpers/config";
import BookingLoginZuumeHeader from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginZuumeHeader";
import CardComponentBlackHeader from "pages/Front/Booking/components/CardComponentBlackHeader";
import BookingLoginReservationButton from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButton";
import ReservationButtonInner from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationButtonInner";
import LoadingIcon from "components/Icons/LoadingIcon";
import FeatherIcon from "components/FeatherIcon";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";

const BookingLoginEmail = (props: {email: string}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const LoginWithCodeSchema = yup.object().shape({
        email: yup
            .string()
            .required("Email is required field.")
            .test("email", "Invalid email format", value => {
                return REGEX_EMAIL.test(value);
            }),
        code: yup
            .string()
            .required("Security code is required field.")
            .test("code-format", `Security code must have ${LOGIN_CODE_LENGTH} digits.`, value => {
                return /^[0-9a-zA-Z]+$/.test(value) && value.length === LOGIN_CODE_LENGTH;
            }),
    });

    const defaultValuesLoginWithCode = {
        email: props.email,
        code: "",
    };

    const {
        register,
        handleSubmit,
        setValue,
        setError,
        formState: {errors},
    } = useForm({
        resolver: yupResolver<any>(LoginWithCodeSchema),
        defaultValues: defaultValuesLoginWithCode,
    });

    const onSubmitError = (data: FieldErrors) => {
        consoleLog("onSubmitError", data);
    };

    const onSubmitLoginWithCodeHandler = (data: {email: string; code: string}) => {
        console.log("onSubmitLoginWithCodeHandler", data);

        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        dispatch(BookingAction.fetchGetLoginWithCode(data.email, data.code))
            .then(
                onResolve => {
                    console.log("onSubmitLoginWithCodeHandler - onResolve", onResolve);
                    dispatch(BookingLoginAction.setReservations(onResolve?.reservations ? onResolve.reservations : []));
                    dispatch(BookingLoginAction.setUser(onResolve));
                    history.replace("/booking/login/reservations");
                },
                onReject => {
                    console.log("onSubmitLoginWithCodeHandler - onReject", onReject);
                    if (onReject === "code_invalid") {
                        // @ts-ignore
                        setError("code", {
                            type: "manual",
                            message: t("Login code invalid"),
                        });
                    }
                }
            )
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    useEffect(() => {
        setValue("email", props.email);
    }, [props.email]);

    return (
        <div className={"w-full flex flex-col justify-center items-center space-y-8"}>
            <BookingLoginZuumeHeader title={t("Login")} />

            <CardComponentBlackHeader className={"w-full max-w-md pb-24"} title={t("Login into your reservation")}>
                <form
                    autoComplete={"off"}
                    className={"LoginForm w-full max-w-xs flex flex-col items-center justify-center"}
                    onSubmit={handleSubmit(onSubmitLoginWithCodeHandler, onSubmitError)}
                >
                    <div className={"my-8 text-center text-xxs md:text-sm font-normal"}>
                        <span className={"text-black"}>{t("The one-time code has been sent to your email address")}</span>
                    </div>
                    <input {...register("email")} className={"w-full"} name={"email"} type={"hidden"} />
                    <BookingLoginInput
                        className={"w-full"}
                        errors={errors}
                        label={t("Verification code")}
                        name={"code"}
                        placeholder={"1234"}
                        register={register}
                        required={true}
                        showIcon={false}
                        type={"text"}
                    />

                    <BookingLoginReservationButton
                        className={"w-full mx-auto mt-6"}
                        disabled={isSubmitting}
                        onClick={handleSubmit(onSubmitLoginWithCodeHandler, onSubmitError)}
                    >
                        <ReservationButtonInner>
                            <span>{t("Login")}</span>
                            {isSubmitting ? (
                                <LoadingIcon className={"stroke-2 size-4"} />
                            ) : (
                                <FeatherIcon name={"check"} strokeWidth={2} className={"stroke-2 size-4"} />
                            )}
                        </ReservationButtonInner>
                    </BookingLoginReservationButton>
                    {errors && <BookingLoginErrors errors={errors} />}
                </form>
            </CardComponentBlackHeader>
        </div>
    );
};

export default BookingLoginEmail;
